"use client";

import React, { useEffect, useRef, useState } from "react";

/*
 * Cooperative auto-height for embedded iframes.
 *
 * Browsers do not let the parent page measure a cross-origin iframe's content
 * height, so true auto-fit requires the embedded page to opt in by sending its
 * height to the parent via window.postMessage. This listener accepts two wire
 * formats:
 *   1. Simple object: { type: "iframe-resize", height: <number> }
 *   2. iframe-resizer v4 string protocol: "[iFrameSizer]<id>:<height>:..."
 *
 * If the embedded page does not cooperate, the iframe stays at DEFAULT_HEIGHT.
 */

const DEFAULT_HEIGHT = 600;
const MIN_HEIGHT = 100;
const MAX_HEIGHT = 10000;

export interface EmbedIFrameAutoHeightProps {
  url: string;
  title?: string;
  width?: string;
  allow?: string;
  sandbox?: string;
}

function clampHeight(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(n)));
}

function parseHeightFromMessage(data: unknown, iframeWindow: Window | null): number | null {
  if (!iframeWindow) return null;

  if (data && typeof data === "object") {
    const obj = data as { type?: unknown; height?: unknown };
    if (obj.type === "iframe-resize") {
      return clampHeight(obj.height);
    }
  }

  if (typeof data === "string" && data.startsWith("[iFrameSizer]")) {
    const parts = data.split(":");
    if (parts.length >= 3) {
      return clampHeight(parts[2]);
    }
  }

  return null;
}

export default function EmbedIFrameAutoHeight(props: EmbedIFrameAutoHeightProps) {
  const { url, title, width, allow, sandbox } = props;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const iframeWindow = iframeRef.current?.contentWindow ?? null;
      if (event.source !== iframeWindow) return;
      const next = parseHeightFromMessage(event.data, iframeWindow);
      if (next !== null) setHeight(next);
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={url}
      title={title}
      width={width || "100%"}
      height={height}
      allow={allow}
      sandbox={sandbox as any}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      style={{ border: 0, width: width || "100%", height: `${height}px` }}
    />
  );
}
